import { useState, useEffect, useRef, RefObject } from 'react';

/**
 * Breakpoint config for home game grid (matches Tailwind and GameProviderCard).
 * Desktop uses smaller card (96px) so 12 cards (4x3) fit without scroll.
 */
function getGridConfig(width: number): { cols: number; cardHeight: number; gap: number } {
  if (width < 640) {
    return { cols: 2, cardHeight: 92, gap: 8 };
  }
  if (width < 768) {
    return { cols: 2, cardHeight: 100, gap: 10 };
  }
  if (width < 834) {
    return { cols: 3, cardHeight: 100, gap: 12 };
  }
  if (width < 1024) {
    return { cols: 3, cardHeight: 96, gap: 12 };
  }
  // Desktop lg (1024px+): 4 columns so 12 cards (4x3) fit
  return { cols: 4, cardHeight: 96, gap: 16 };
}

/**
 * Computes how many game cards fit in the given height without scrolling.
 * rows = floor((availableHeight + gap) / (cardHeight + gap)), then cardsPerPage = cols * rows.
 */
function computeCardsPerPage(
  width: number,
  height: number
): number {
  const availableHeight = height;
  if (availableHeight <= 0) return 12;
  const { cols, cardHeight, gap } = getGridConfig(width);
  const rowHeight = cardHeight + gap;
  const rows = Math.max(1, Math.floor((availableHeight + gap) / rowHeight));
  return cols * rows;
}

const FALLBACK_CARDS_PER_PAGE = 12;

/**
 * Uses ResizeObserver on the grid container to compute how many cards fit
 * in the available height so the home page doesn't need to scroll.
 */
export function useHomeGridCapacity(containerRef: RefObject<HTMLDivElement | null>): number {
  const [cardsPerPage, setCardsPerPage] = useState(FALLBACK_CARDS_PER_PAGE);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const { width, height } = el.getBoundingClientRect();
        const next = computeCardsPerPage(width, height);
        setCardsPerPage((prev) => (next > 0 ? next : prev));
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef]);

  return cardsPerPage;
}
